import { useMemo } from "react";
import type {
  CustodianCode,
  FirmRiskProfile,
} from "../../../api/src/firms/firms.service";
import type { SerializedObject, UnpackResponse } from "../../../api/src/lib";
import type { UsersController } from "../../../api/src/users/users.controller";
import type { CrmUser } from "../../../api/src/users/users.service";
import { deserializeDate, useAuthenticatedQuery } from "../lib/api";
import { useAuthenticatedFetchAllPages } from "../lib/pagination";

/**
 * The route the console READS a firm's risk levels from.
 *
 * Published at `/v1` by APP-1563, in the same change that paginated it — the
 * named change `docs/adr/firm-domain-admission.md` records against it on
 * criterion C8. It is also the FIRST element of every react-query key the walk
 * below uses, so an invalidation after a save reaches every page of the
 * collection rather than only the one whose url it happens to spell.
 *
 * `POST /v1/firm/risk`, `PUT /v1/firm/risk/:id` and `DELETE /v1/firm/risk/:id`
 * are deliberately NOT written through this constant. They were versioned by an
 * earlier job, each answers with a single risk level rather than the
 * collection, and none of them is paginated; the create posts to this very path
 * — same string, different method — and sharing the constant would tie a
 * write's url to a read's pagination key for no reason beyond the two happening
 * to spell the same thing today.
 *
 * This is an API path. The react-router entries that read `/firm/risk` —
 * `FirmNav.tsx`, `Routes.tsx`, `help/contextMap.ts` and the `<Link>` on
 * `FirmRiskAssetClass.tsx` — are browser routes and do NOT move with it.
 */
export const FIRM_RISK_PROFILES_PATH = "/v1/firm/risk";

/**
 * Every risk level a firm has, walked a page at a time.
 *
 * WHY THIS IS A SHARED HOOK AND NOT A WALK PER SCREEN
 *
 * Four screens read this one collection, and three of them are nowhere near
 * `client/src/firm`: `clients/account/AccountPortfolio.tsx`,
 * `clients/household/HouseholdHoldings.tsx` and
 * `rebalances/RebalanceHoldings.tsx` each turn the risk levels into the row
 * groups of a holdings table. A holdings screen that read the first fifty of a
 * firm's risk levels and treated them as all of them would drop every holding
 * allocated only to the rest — it would misreport allocation, silently, and
 * `tsc` cannot see it because a page and the whole collection have the same
 * element type. One walk, written once, is what keeps the four of them from
 * drifting apart.
 *
 * The walk itself is `useAuthenticatedFetchAllPages` in `../lib/pagination`;
 * this hook names the path and deserialises the dates. `reload` restarts the
 * walk, for a screen that WRITES to the collection; read-only screens never
 * call it.
 */
export function useFirmRiskProfiles() {
  const {
    rows,
    hasEveryRow: hasEveryRiskProfile,
    isPendingFirstPage,
    isError,
    reload,
  } = useAuthenticatedFetchAllPages<SerializedObject<FirmRiskProfile>>(
    FIRM_RISK_PROFILES_PATH,
  );

  const riskProfiles = useMemo(
    () =>
      rows.map((profile) => ({
        ...profile,
        createdTime: deserializeDate(profile.createdTime),
        updatedTime: deserializeDate(profile.updatedTime),
      })),
    [rows],
  );

  return {
    /** Every risk level collected so far, dates deserialised. */
    riskProfiles,
    /** True once the walk holds as many rows as the sibling key reports. */
    hasEveryRiskProfile,
    /** True only while the FIRST page is outstanding. */
    isPendingFirstPage,
    isError,
    /** Drop what has been collected, so the walk starts again at offset 0. */
    reload,
  };
}

/**
 * The Schwab advisor codes a firm can block-trade through: still open, and at
 * Schwab.
 *
 * A null `closeDate` is a code that has not been retired, so it belongs here.
 * The null has to be tested rather than left to the comparison: `null > new
 * Date()` coerces the null to 0 and is false, which would empty the selector of
 * every open code and leave only codes retired in the future — of which there
 * are none.
 *
 * Lifted out of `FirmInfo`'s `useMemo` so the rule can be asserted on its own,
 * without a firm, a form or a fetch.
 */
export function activeSchwabAdvisorCodes<
  T extends { custodian: CustodianCode; closeDate: Date | null },
>(advisorCodes: T[]): T[] {
  return advisorCodes.filter(
    (ac) =>
      ac.custodian === "SWB" &&
      (ac.closeDate === null || ac.closeDate > new Date()),
  );
}

/**
 * Which household a control offering assignees sits on. A household id only —
 * never a CRM. The server reads that household's CRM from its link tables, so
 * the resolution rule lives on the server alone and no caller can ask for a CRM
 * the household is not linked to
 * (s-task-assignee-list-source-019fd301825de871, R1/R3a).
 *
 * The household is the only thing that carries the link. Accounts and contacts
 * have none of their own to offer here and pass the household they belong to.
 */
export type CrmUsersRecord = { householdId: number };

/**
 * CRM users offered as assignees, from one CRM rather than Wealthbox alone
 * (s-task-assignee-list-source-019fd301825de871).
 *
 * `record` scopes the list to one household, for a control sitting on it: a
 * household's controls follow that household's link (R1). Left unset —
 * for list pages, filters and exports, which span households and so have no one
 * record to follow — the server derives the firm's CRM instead: Slant, then
 * Wealthbox, then Redtail, first connected wins (R2).
 *
 * The query errors when the CRM is connected but offers nobody. That is a
 * fault, not an empty firm, so a caller offering these as a control must show
 * the error rather than an empty list.
 */
export function useQueryCrmUsers({
  page = 1,
  enabled = true,
  record,
}: { page?: number; enabled?: boolean; record?: CrmUsersRecord } = {}) {
  const recordQuery =
    record === undefined ? "" : `&householdId=${record.householdId}`;

  return useAuthenticatedQuery<
    UnpackResponse<UsersController["getAllCrmUsers"]>,
    { data: CrmUser[]; page: number },
    [string, string, number, string]
  >({
    queryKey: ["users", "crm", page, recordQuery],
    queryFn: async (users) => ({
      data: users.data,
      page,
    }),
    urlBuilderFn: (key) => `/${key[0]}/${key[1]}?page=${key[2]}${key[3]}`,
    queryOptions: { enabled },
  });
}

export const securityCategoryAssetClassColumnsMap = new Map(
  Object.entries({
    usLargeCapEquity: {
      col: "us_large_cap_equity",
      label: "US Large Cap Equity",
    },
    usSmallCapEquity: {
      col: "us_small_cap_equity",
      label: "US Small Cap Equity",
    },
    devUsEquity: {
      col: "dev_us_equity",
      label: "Developed ex US Equity",
    },
    marketEquity: {
      col: "market_equity",
      label: "Emerging Markets Equity",
    },
    realEstate: {
      col: "real_estate",
      label: "Real Estate",
    },
    commoditiesAlternatives: {
      col: "commodities_alternatives",
      label: "Commodoties & Alternatives",
    },
    usShortTermBonds: {
      col: "us_short_term_bonds",
      label: "US Short-Term Bonds",
    },
    intermediateTermBonds: {
      col: "intermediate_term_bonds",
      label: "US Intermediate-Term Bonds",
    },
    usLongTermBonds: {
      col: "us_long_term_bonds",
      label: "US Long-Term Bonds",
    },
    inflationLinkedBonds: {
      col: "inflation_linked_bonds",
      label: "Inflation-Linked Bonds",
    },
    usHighYieldBonds: {
      col: "us_high_yield_bonds",
      label: "US High-Yield Bonds",
    },
    usTaxExemptBonds: {
      col: "us_tax_exempt_bonds",
      label: "US Tax Exempt Bonds",
    },
    foreignBonds: {
      col: "foreign_bonds",
      label: "Foreign Bonds",
    },
    cashEquivalents: {
      col: "cash_equivalents",
      label: "Cash & Cash Equivalents",
    },
  }),
);
